import { StrictMode, useEffect } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { ConfigProvider, App as AntdApp } from "antd";
import zhTW from "antd/locale/zh_TW";
import App from "./App.tsx";
import "./index.css";
import "./styles/keyboard-fix.css"; // iOS Safari 键盘适配
import "@ifa/ui";
import { MessageBridge } from "./components/MessageBridge";
import {
  setSelectMetadataProvider,
  setCountryProvider,
  setBrandAssetsProvider,
  useMetadataStore,
  useCountryStore,
  useConfigStore,
  initSSOClient,
  ssoLogout,
  config,
  setTokenProvider,
  setTenantCodeProvider,
  setUnauthorizedHandler,
  type ExchangeTokenResult,
} from "@ifa/utils";
import { useThemeStore, initializeTheme, loadThemeConfig } from "./theme";
import { getBrandAssets } from "@ifa/ui";


// 当前应用标识
const APP_CODE = "ADVISOR_SMART_PORTAL";

// localStorage 工具函数
const getToken = () => localStorage.getItem("token");
const getTenantCode = () => localStorage.getItem("tenantCode");
const saveTenantCode = (tenantCode: string) => {
  localStorage.setItem("tenantCode", tenantCode);
};
const clearAuth = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("uid");
  localStorage.removeItem("tenantCode");
};
const saveAuth = (result: ExchangeTokenResult) => {
  localStorage.setItem("token", result.token);
  localStorage.setItem("uid", String(result.uid));
};

// 获取当前页面 URL（用于 SSO 回调）
// 重要：必须返回当前真实的 URL，而不是固定值
const getCurrentUrl = () => window.location.href;

// 初始化 SSO 客户端
initSSOClient({
  authUrl: config.authUrl,
  app: APP_CODE,
  getToken,
  saveAuth,
  clearAuth,
  saveTenantCode,
  getCurrentUrl,
});

// 注入 token 获取方式
setTokenProvider(() => getToken() || "");

// 注入租户代码获取方式
setTenantCodeProvider(() => getTenantCode() || "");

// 注入未授权处理：使用 SSO 登出
setUnauthorizedHandler(() => {
  ssoLogout();
});

// 初始化全局提供者
setSelectMetadataProvider(() => useMetadataStore.getState().selectMetadataList);
setCountryProvider(() => useCountryStore.getState().allCountries);

// 注入品牌资源提供者
setBrandAssetsProvider(getBrandAssets);

// 初始化全局配置（基于当前域名）
useConfigStore.getState().initializeFromCurrentDomain();

// 初始化主题（应用 CSS 变量）
initializeTheme();

// 异步加载主题配置（如果需要从 API 获取）
loadThemeConfig().catch((error) => {
  console.error("Failed to load theme config:", error);
});

/**
 * 主题桥接组件
 * 监听主题变化并更新 Ant Design ConfigProvider
 */
function ThemeBridge({ children }: { children: React.ReactNode }) {
  const antdTheme = useThemeStore((state) => state.antdTheme);

  // 扩展主题配置，添加统一字体（tokens 已包含正确的颜色、字号、圆角）
  const themeWithFont = {
    ...antdTheme,
    token: {
      ...antdTheme.token,
      fontFamily: 'NotoSans, "Noto Sans"',
      // 设置全局 zIndex，确保 message 提示框在最上层
      zIndexPopupBase: 100002,
    },
  };

  return (
    <ConfigProvider locale={zhTW} theme={themeWithFont}>
      <AntdApp>
        <MessageBridge />
        {children}
      </AntdApp>
    </ConfigProvider>
  );
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <BrowserRouter>
      <ThemeBridge>
        <App />
      </ThemeBridge>
    </BrowserRouter>
  </StrictMode>
);
